import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, FileCheck2, UserCheck, ScrollText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";

const POINTS = [
  [FileCheck2, "GSTR-1, GSTR-3B, GSTR-9 and ITC-04", "Prepare, review and file returns for every client from one place."],
  [UserCheck, "Two-level approval", "Every return is prepared by one person and approved by a partner before filing."],
  [ScrollText, "Complete audit trail", "All actions and approvals are recorded for review."],
];

const inputCls = "mt-1.5 w-full rounded-md bg-card border border-input px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600/30 focus:border-indigo-600 transition-colors";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) { toast.success("Signed in"); nav("/"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-white" style={{ backgroundColor: "#10264E" }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative flex items-center gap-3">
          <Logo size={48} withText={false} />
          <div>
            <div className="font-head text-xl font-semibold tracking-tight">Ameya Management Group</div>
            <div className="text-sm text-white/70">GSTDesk · GST Compliance Portal</div>
          </div>
        </div>
        <div className="relative max-w-lg space-y-8">
          <h1 className="font-head text-4xl font-semibold leading-tight">GST compliance for all your clients, in one secure portal.</h1>
          <div className="space-y-5">
            {POINTS.map(([Icon, title, text]) => (
              <div key={title} className="flex gap-4">
                <div className="h-10 w-10 shrink-0 rounded-md bg-white/10 border border-white/15 flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div><div className="font-medium">{title}</div><div className="text-sm text-white/70">{text}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/60">© {new Date().getFullYear()} Ameya Management Group. For authorised users only.</div>
      </div>

      <div className="relative flex items-center justify-center p-6">
        <ThemeToggle className="absolute top-5 right-5" />
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="lg:hidden"><Logo size={40} subtitle="Ameya Management Group" /></div>
          <div>
            <h2 className="font-head text-2xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your Ameya Management Group account.</p>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="email">Email address</label>
            <input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className={inputCls} placeholder="name@ameyaidt.com" />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="password">Password</label>
            <input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className={inputCls} placeholder="Enter your password" />
          </div>
          <button data-testid="login-submit-button" disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white py-2.5 text-sm font-medium transition-colors">
            <Lock className="h-4 w-4" /> {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-xs text-muted-foreground border-t border-border pt-4">Authorised personnel only. All sign-ins and filing actions are recorded for audit.</p>
        </form>
      </div>
    </div>
  );
}
